import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Building2, ChevronRight, FileQuestion, Plus } from "lucide-react";
import { Link } from "react-router";
import { Button, PageContainer } from "@repo/ui";
import { CarteOrganisation } from "./CarteOrganisation";

export function OrgsDashboard() {
  const orgs = useQuery(api.superAdmin.listerOrganisationsAvecStats);

  if (orgs === undefined) {
    return (
      <div className="flex justify-center p-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const actives = orgs.filter((o) => o.archivedAt === undefined);
  const archivees = orgs.filter((o) => o.archivedAt !== undefined);

  return (
    <PageContainer>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Organisations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {actives.length} organisation(s)
            {archivees.length > 0 && ` · ${archivees.length} archivée(s)`}
          </p>
        </div>
        <div className="flex items-center gap-2">
        <Button asChild variant="outline">
          <Link to="/scorecards">Modèles de scorecards</Link>
        </Button>
        <Button asChild>
          <Link to="/organisations/creer">
            <Plus size={14} />
            Nouvelle organisation
          </Link>
        </Button>
        </div>
      </div>

      <div className="space-y-3">
        {actives.map((org) => (
          <CarteOrganisation key={org._id} org={org} />
        ))}

        {/* Données d'avant le multi-tenant : campagnes et parcours sans
            `organisationId`. Sans cette entrée, la navigation partant toujours
            d'une organisation, elles ne seraient plus atteignables. Aucun
            compteur : les calculer voudrait dire lire des parcours au
            chargement du dashboard, ce qu'on vient justement de supprimer. */}
        <div className="bg-card border border-dashed border-charte-line rounded-surface p-5 flex items-center justify-between transition-colors duration-150 hover:border-charte-line-accent">
          <div className="flex items-center gap-4">
            <div className="bg-muted p-2.5 rounded-controle">
              <FileQuestion size={20} className="text-muted-foreground" />
            </div>
            <div>
              <span className="font-semibold">Sans organisation</span>
              <p className="text-sm text-muted-foreground mt-0.5">
                Campagnes et parcours d'avant le multi-tenant
              </p>
            </div>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/sans-organisation/campagnes">Campagnes</Link>
          </Button>
        </div>

        {orgs.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Building2 size={40} className="mx-auto mb-3 opacity-30" />
            <p>Aucune organisation. Créez la première.</p>
          </div>
        )}

        {/* Repliée par défaut : les archivées restent atteignables (corbeille,
            désarchivage) sans encombrer la liste de travail. */}
        {archivees.length > 0 && (
          <details className="group pt-3">
            <summary className="flex w-fit cursor-pointer list-none items-center gap-1.5 rounded-controle text-sm font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
              <ChevronRight size={14} className="transition-transform duration-150 group-open:rotate-90" />
              Archivées ({archivees.length})
            </summary>
            <div className="mt-3 space-y-3 opacity-70">
              {archivees.map((org) => (
                <CarteOrganisation key={org._id} org={org} />
              ))}
            </div>
          </details>
        )}
      </div>
    </PageContainer>
  );
}
